import React from 'react';

import { useFieldContext, FieldErrors } from '../';
import { Input, Label } from '../../Elements/';

type TextFieldProps = {
	label: string;
	icon?: React.ReactElement;
	required?: boolean;
	labelVisible?: boolean;
	className?: string;
	errorClassName?: string;
	// React 19 passes ref as a regular prop; it reaches the Input via the spread inputProps
	ref?: React.Ref<HTMLInputElement>;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const TextField = ({
	icon,
	label,
	required,
	labelVisible = true,
	className,
	errorClassName,
	id,
	onBlur,
	...inputProps
}: TextFieldProps) => {
	const field = useFieldContext<string>();

	const fieldId = id || field.name;
	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let newVal = e.target.value;
		if (inputProps.inputMode === 'numeric') {
			// allow only numeric input
			newVal = newVal.replace(/[^0-9.-]+/g, '');
		}
		field.handleChange(newVal);
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		field.handleBlur();
		onBlur?.(e);
	};

	// hide the error messages until the field has been touched
	// only show errors after initial blur to prevent the error from displaying when first typing, meta.isBlurred is set to true after initial blur
	const hideErrors = !field.state.meta.isTouched || !field.state.meta.isBlurred;
	const hasErrors = field.state.meta.errors.length > 0 && !hideErrors;

	return (
		<>
			<div className={`flex flex-row ${!labelVisible ? 'sr-only' : ''}`}>
				<Label
					htmlFor={fieldId}
					label={label}
					className='mb-2'
					required={required}
					disabled={inputProps.disabled}
				/>
				{icon}
			</div>
			<Input
				value={field.state.value || ''}
				onChange={onChange}
				onBlur={handleBlur}
				{...inputProps}
				name={field.name}
				id={fieldId}
				hasErrors={hasErrors}
				required={required}
				className={className}
			/>
			<FieldErrors
				meta={field.state.meta}
				name={field.name}
				hideErrors={hideErrors}
				className={errorClassName}
			/>
		</>
	);
};
